import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * How long to hold the buy buttons before releasing them regardless.
 *
 * variant-picker.js aborts an in-flight request when a shopper switches again
 * (assets/variant-picker.js:312), and an aborted request's promise may never
 * settle at all. Without a backstop that would leave the buttons disabled for
 * good — blocking every purchase on the page, which is far worse than the bug
 * this guard exists to fix. The real window measured ~400ms.
 */
const BUY_BUTTON_RELEASE_MS = 5000;

/**
 * @typedef {object} KitCustomizerRefs
 * @property {HTMLElement} [summary] - The summary line on the trigger row.
 * @property {HTMLInputElement} [variantIds] - Hidden input recording the chosen component variant ids.
 * @property {HTMLInputElement} [bundleSelection] - Simple Bundles' _bundle_selection record.
 */

/**
 * Delimiters Simple Bundles' own widget uses to compose properties[_bundle_selection].
 * Read from cdn.shopify.com/extensions/.../simple-bundles-v2.min.js, which does:
 *   variant_options_v2.map(g => g.map(o => value).join(" ++ ")).join(" <> ")
 * These are internal to the app and not publicly documented, so #syncBundleSelection
 * validates its own output rather than trusting them silently.
 */
const BUNDLE_GROUP_SEPARATOR = ' <> ';
const BUNDLE_WITHIN_GROUP_SEPARATOR = ' ++ ';

/**
 * Kit customizer.
 *
 * Deliberately does NOT touch the cart. The component <select>s carry
 * name="properties[…]" and form="BuyButtons-ProductForm-<section.id>" themselves, so
 * assets/product-form.js:420 picks them up via FormData on the normal single-line
 * submit path. This element only has to:
 *
 *   1. keep the trigger row's summary text in step with the choices,
 *   2. swap each row's thumbnail to the chosen variant's image,
 *   3. disable the property controls when the base tier is selected, so they drop
 *      out of FormData entirely and a plain cup never carries kit properties.
 *      Same technique as assets/gift-card-recipient-form.js:214-234.
 *
 * NOTE ON RE-RENDERS: morph patches this element's CHILDREN rather than replacing the
 * element, so connectedCallback does NOT run again. An earlier version of this comment
 * claimed it did, and a whole MutationObserver was built on that false premise — it hung
 * every bundle product and fixed nothing. Listeners are bound on the element itself, which
 * survives, so nothing needs re-wiring.
 *
 * @extends {Component<KitCustomizerRefs>}
 */
class KitCustomizer extends Component {
  connectedCallback() {
    super.connectedCallback();


    // Component selects live inside this element, so their change events bubble here.
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('click', this.#handleCardClick);
    window.addEventListener('resize', this.#syncRails, { passive: true });
    this.#syncRails();

    // Tier radios live in the variant picker, outside this element — listen higher up.
    this.#scope?.addEventListener('change', this.#handleScopeChange);
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    // At >=990px assets/theme-drawer.js:153 opens the panel with show(), not
    // showModal(), so there is no ::backdrop for the browser to route a dismiss click
    // through — and our dim is a box-shadow, which takes no pointer events. Below that
    // it is a real modal and the theme handles it, hence the panel.open guard plus the
    // contains() test rather than a blanket close.
    document.addEventListener('pointerdown', this.#handleOutsidePointer);

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('click', this.#handleCardClick);
    window.removeEventListener('resize', this.#syncRails);
    this.#scope?.removeEventListener('change', this.#handleScopeChange);
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.removeEventListener('pointerdown', this.#handleOutsidePointer);
  }

  /** @param {PointerEvent} event */
  #handleOutsidePointer = (event) => {
    const drawer = this.querySelector('theme-drawer');
    const panel = this.querySelector('dialog.kit-drawer');
    if (!(panel instanceof HTMLDialogElement) || !panel.open) return;

    const target = event.target;
    if (!(target instanceof Node) || panel.contains(target)) return;

    // The trigger row toggles the drawer itself; closing here too would race it and the
    // drawer would reopen-then-close on a single click.
    if (target instanceof Element && target.closest('.kit-customizer__row')) return;

    /** @type {{ close?: () => void }} */ (drawer)?.close?.();
  };

  get #scope() {
    return this.closest('.shopify-section, dialog');
  }

  /**
   * EVERY control that carries a properties[…] name, because #sync toggles `disabled`
   * across exactly this set and a disabled control is invisible to FormData.
   *
   * Missing one is silent and expensive. This list used to omit [data-kit-display] and
   * bundleSelection, which was harmless only while the kit tier happened to be the
   * default — Liquid then rendered everything enabled and the gap never showed. The
   * moment Single CrewCup came into stock it became the default tier, so pages started
   * loading with all eight inputs disabled, and clicking the kit tier re-enabled just
   * the three <select>s. Those submit under Simple Bundles' raw selector ids, which
   * snippets/cart-products.liquid hides from the cart, so the order carried no readable
   * choices AND no _bundle_selection — meaning the app could not split it into
   * component SKUs. A full-price kit that ships as a bare cup.
   */
  get #inputs() {
    // Selected on the property name itself rather than an enumerated list of
    // data-attributes, so any control added to this component later is covered without
    // anyone having to remember to update this getter.
    //
    // data-kit-name is the SAME set, parked. On the base tier the name attribute is
    // removed outright (see #setSubmittable), so matching only [name^="properties["]
    // would find nothing and the names could never be put back.
    return /** @type {(HTMLSelectElement|HTMLInputElement)[]} */ ([
      ...this.querySelectorAll('[name^="properties["], [data-kit-name]'),
    ]);
  }

  /**
   * Attach or detach one control from the form.
   *
   * `disabled` alone is not enough. It hides a control from FormData and nothing
   * more — the name and the value stay in the DOM, so any code that reads inputs
   * directly still sees the shopper's charm and boot. Shopify's accelerated
   * checkout (Buy with Shop) is not our code and we cannot verify how it
   * serialises, so the guarantee has to hold regardless of how it reads.
   *
   * Removing the name attribute is that guarantee: a control with no name is
   * invisible to every serialiser there is. The name is parked in data-kit-name
   * and restored on the kit tier, and the value is never touched, so parking
   * costs the shopper nothing — their selection is still there when they switch
   * back.
   *
   * snippets/kit-bundle-rows.liquid renders the same parked shape server-side, so
   * this and the server agree and morph cannot reintroduce a live name.
   *
   * @param {HTMLSelectElement|HTMLInputElement} input
   * @param {boolean} submittable
   */
  #setSubmittable(input, submittable) {
    if (submittable) {
      const parked = input.dataset.kitName;
      if (parked != null) {
        input.setAttribute('name', parked);
        delete input.dataset.kitName;
      }
      input.disabled = false;
      return;
    }

    const name = input.getAttribute('name');
    if (name != null) {
      input.dataset.kitName = name;
      input.removeAttribute('name');
    }
    input.disabled = true;
  }

  /**
   * True when kit data should submit: the upgrade tier is selected, or this product has
   * no tiers because the product itself IS the kit.
   *
   * The no-tier case is read from the class Liquid sets, not inferred from an absent
   * radio. Inferring it the other way round — no checked radio means not upgraded —
   * silently disabled every property input on a Simple Bundles product that has no
   * Package option, which is a shape the rollout can produce.
   */
  get #isUpgrade() {
    if (this.classList.contains('kit-customizer--no-tier')) return true;
    const checked = this.#scope?.querySelector('.kit-tier__input:checked');
    return checked instanceof HTMLInputElement && checked.dataset.tier === 'full';
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    this.#updateThumbnail(event.target);
    this.#updateSummary();
    this.#updateVariantIds();
    this.#syncBundleSelection();
    this.#syncCards();
    this.#syncRails();
  };

  /**
   * The cards are a view over each row's hidden <select>. Clicking one writes the value
   * to the select and dispatches a bubbling `change`, so #handleChange runs exactly as it
   * does for a native select and every downstream property — the summary, the thumbnail,
   * _kit_variant_ids and _bundle_selection — is produced by the same code path as before.
   *
   * @param {Event} event
   */
  #handleCardClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Scroll controls share this delegated handler but must not fall through to the card
    // branch, or clicking an arrow would also pick whatever card sits under it.
    const nav = target.closest('[data-kit-scroll]');
    if (nav instanceof HTMLButtonElement) {
      const rail = nav.closest('[data-kit-rail]');
      const cards = rail?.querySelector('.kit-cards');
      if (cards instanceof HTMLElement) {
        const dir = Number(nav.dataset.kitScroll) || 1;
        // 80% of the visible width: enough to feel like a page, little enough that a
        // partially visible card stays on screen as an anchor.
        cards.scrollBy({ left: dir * cards.clientWidth * 0.8 });
      }
      return;
    }

    const card = target.closest('[data-kit-card]');
    if (!(card instanceof HTMLButtonElement) || card.disabled) return;

    const row = card.closest('[data-kit-component]');
    const select = row?.querySelector('select[data-kit-select]');
    if (!(select instanceof HTMLSelectElement)) return;

    const { value } = card.dataset;
    if (value == null || select.value === value) return;

    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Shows or hides each rail's controls based on whether the row can actually scroll.
   * Called on connect, after any sync, and on scroll — the row's overflow depends on the
   * viewport, so it cannot be decided server-side.
   */
  #syncRails = () => {
    for (const rail of this.querySelectorAll('[data-kit-rail]')) {
      const cards = rail.querySelector('.kit-cards');
      const prev = rail.querySelector('[data-kit-scroll="-1"]');
      const next = rail.querySelector('[data-kit-scroll="1"]');
      if (!(cards instanceof HTMLElement)) continue;

      // 1px of slack: scrollWidth and clientWidth differ by sub-pixel amounts at some
      // zoom levels, which would leave the "next" control showing at the very end.
      const overflowing = cards.scrollWidth - cards.clientWidth > 1;
      const atStart = cards.scrollLeft <= 1;
      const atEnd = cards.scrollLeft >= cards.scrollWidth - cards.clientWidth - 1;

      if (prev instanceof HTMLElement) prev.hidden = !overflowing || atStart;
      if (next instanceof HTMLElement) next.hidden = !overflowing || atEnd;

      if (!cards.dataset.railBound) {
        cards.dataset.railBound = 'true';
        cards.addEventListener('scroll', this.#syncRails, { passive: true });
      }
    }
  };

  /** Mirrors each row's selected value onto its cards. */
  #syncCards() {
    for (const row of this.querySelectorAll('[data-kit-component]')) {
      const select = row.querySelector('select[data-kit-select]');
      if (!(select instanceof HTMLSelectElement)) continue;

      for (const card of row.querySelectorAll('[data-kit-card]')) {
        if (!(card instanceof HTMLButtonElement)) continue;
        card.setAttribute('aria-pressed', String(card.dataset.value === select.value));
        // The select is disabled on the base tier, so the cards must follow or they
        // would look operable while writing to a control that submits nothing.
        card.disabled = select.disabled;
      }
    }
  }

  /** @param {Event} event */
  #handleScopeChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.classList.contains('kit-tier__input')) return;
    this.#sync();
  };

  #sync() {
    const active = this.#isUpgrade;

    // Detached entirely on the base tier, not merely disabled — see
    // #setSubmittable for why `disabled` is not a strong enough guarantee.
    for (const input of this.#inputs) this.#setSubmittable(input, active);

    for (const select of this.querySelectorAll('[data-kit-select]')) {
      if (select instanceof HTMLSelectElement) this.#updateThumbnail(select);
    }

    this.#updateSummary();
    this.#updateVariantIds();
    this.#syncBundleSelection();
    this.#syncCards();
    this.#syncRails();
  }

  /** True when the selectors come from Simple Bundles rather than child blocks. */
  get #isBundleMode() {
    return this.dataset.bundleMode === 'true';
  }

  /**
   * Compose properties[_bundle_selection] the way the app's widget does, then verify
   * it before letting it stand. If the shape is wrong the input is cleared and the
   * add-to-cart button is disabled — better a blocked purchase than an order the app
   * cannot break into components, which would ship as a bare product.
   */
  /**
   * The _bundle_selection input, found by NAME rather than through refs.
   *
   * refs cannot reach it. assets/component.js:171 claims a [ref] element only when its
   * closest ancestor component is this one, and the input lives inside <theme-drawer>, which
   * is itself a Component — so the ref belongs to the drawer. this.refs.bundleSelection was
   * therefore undefined, #syncBundleSelection returned on its first line, and the readable
   * duplicates plus _bundle_selection kept their server-rendered defaults while the selects
   * held the shopper's real choice. That is the wrong-add-ons bug.
   *
   * The summary escaped it only because ref="summary" sits in the trigger row, outside the
   * drawer. querySelector ignores component boundaries, so it works from either side.
   */
  get #bundleSelectionInput() {
    // Both spellings: on the base tier the name is parked in data-kit-name, so a
    // name-only lookup would miss it and #syncBundleSelection would silently stop
    // clearing the record.
    const el = this.querySelector(
      'input[name="properties[_bundle_selection]"], input[data-kit-name="properties[_bundle_selection]"]'
    );
    return el instanceof HTMLInputElement ? el : null;
  }

  #syncBundleSelection() {
    const bundleSelection = this.#bundleSelectionInput;
    if (!bundleSelection || !this.#isBundleMode) return;

    // The base tier is a plain cup — it carries no bundle and must stay purchasable
    // whatever the kit's state. This matters during rollout: a product that has the
    // Package option but is not yet registered in Simple Bundles renders zero rows,
    // which the validation below reads as an incomplete selection. Without this the
    // block would fall on the whole product and the single cup would stop selling.
    if (!this.#isUpgrade) {
      bundleSelection.value = '';
      this.#setPurchasable(true);
      return;
    }

    // Group by source product, preserving DOM order — which is the metafield order,
    // which is the order the app joins in.
    /** @type {Map<string, string[]>} */
    const groups = new Map();
    let count = 0;
    let allFilled = true;

    for (const row of this.querySelectorAll('[data-bundle-group]')) {
      const select = row.querySelector('[data-bundle-option]');
      if (!(select instanceof HTMLSelectElement)) continue;

      const key = /** @type {HTMLElement} */ (row).dataset.bundleGroup ?? String(count);
      const value = select.value.trim();
      if (!value) allFilled = false;

      const bucket = groups.get(key);
      if (bucket) bucket.push(value);
      else groups.set(key, [value]);
      count += 1;
    }

    const composed = [...groups.values()]
      .map((vals) => vals.join(BUNDLE_WITHIN_GROUP_SEPARATOR))
      .join(BUNDLE_GROUP_SEPARATOR);

    const valid = count > 0 && allFilled;

    // Mirror each choice into its readable duplicate, which is what the cart shows.
    for (const row of this.querySelectorAll('[data-bundle-group]')) {
      const select = row.querySelector('[data-bundle-option]');
      const display = row.querySelector('[data-kit-display]');
      if (select instanceof HTMLSelectElement && display instanceof HTMLInputElement) {
        display.value = select.value;
      }
    }

    bundleSelection.value = valid ? composed : '';
    this.#setPurchasable(valid);
  }

  /**
   * THE VARIANT / PROPERTIES DESYNC WINDOW.
   *
   * Clicking a tier updates two things at two different speeds:
   *
   *   - the tier radio and the property inputs, synchronously, in #sync below
   *   - the form's own <input name="id">, only once Horizon's server-rendered
   *     section lands, measured at ~400ms on a live product page
   *
   * For that ~400ms the form disagrees with itself, and every buy button
   * serialises the form exactly as it stands. Both directions produce a bad
   * order, and both have been reproduced against the live site:
   *
   *   kit -> base : form still holds the KIT variant, properties already OFF
   *                 => a kit with no charm and no boot, which cannot be packed
   *   base -> kit : form still holds the BASE variant, properties already ON
   *                 => a plain cup carrying kit properties, which is the shape
   *                    of the orders that prompted this
   *
   * Shop Pay is what turns it from theoretical into observed: it skips the cart
   * entirely, so there is no drawer in which a shopper might notice the mistake,
   * and it is the quickest thing on the page to reach.
   *
   * Horizon does not guard this itself — variant-picker.js starts the fetch and
   * leaves the buttons live throughout. So hold the buy buttons for the duration
   * of that fetch. variant-picker.js:318 dispatches StandardEvents.productSelect
   * when it begins, carrying a promise that settles when the new section lands,
   * which is precisely the window to cover.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    // Quick-add renders a whole product form inside a card, and its variant
    // changes are none of our business. Same test product-price.js:40 uses.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // A generation token rather than a counter: when a shopper switches twice
    // quickly the first request is aborted, and only the newest request should
    // be allowed to release the buttons. A counter would let the stale
    // request's settlement re-enable them while the newer one is still in
    // flight — reopening the window this is here to close.
    const generation = ++this.#selectGeneration;
    this.#setBuyButtonsPending(true);

    const release = () => {
      if (generation === this.#selectGeneration) this.#setBuyButtonsPending(false);
    };

    event.promise.then(release, release);
    setTimeout(release, BUY_BUTTON_RELEASE_MS);
  };

  #selectGeneration = 0;

  /**
   * Hold or release the buy buttons.
   *
   * Ownership is tracked with a data flag, the same way #setPurchasable does it,
   * so this can never re-enable a control that was disabled for some other
   * reason — sold out, or an incomplete bundle selection. It only ever releases
   * what it took.
   *
   * @param {boolean} pending
   */
  #setBuyButtonsPending(pending) {
    const formId = this.dataset.formId;
    const form = formId ? document.getElementById(formId) : null;
    if (!form) return;

    const button = form.querySelector('button[name="add"]');
    if (button instanceof HTMLButtonElement) {
      if (pending) {
        if (!button.disabled) {
          button.disabled = true;
          button.dataset.kitPending = 'true';
        }
      } else if (button.dataset.kitPending === 'true') {
        button.disabled = false;
        delete button.dataset.kitPending;
      }
    }

    // Shop Pay and the other accelerated buttons. There is no `disabled` to set:
    // <shopify-accelerated-checkout> is Shopify's own element and renders into a
    // shadow root we neither own nor should reach into. `inert` is the right
    // tool — it blocks pointer events, focus and activation for a subtree,
    // shadow content included, without us touching their markup.
    const accelerated = form.querySelector('.accelerated-checkout-block, [data-shopify="payment-button"]');
    if (accelerated instanceof HTMLElement) {
      if (pending) {
        if (!accelerated.inert) {
          accelerated.inert = true;
          accelerated.dataset.kitPending = 'true';
          accelerated.classList.add('kit-buy-pending');
        }
      } else if (accelerated.dataset.kitPending === 'true') {
        accelerated.inert = false;
        delete accelerated.dataset.kitPending;
        accelerated.classList.remove('kit-buy-pending');
      }
    }
  }

  /**
   * @param {boolean} ok - Whether the current selection is safe to submit.
   */
  #setPurchasable(ok) {
    const formId = this.dataset.formId;
    const form = formId ? document.getElementById(formId) : null;
    const button = form?.querySelector('button[name="add"]');
    if (!(button instanceof HTMLButtonElement)) return;

    if (ok) {
      if (button.dataset.kitBlocked === 'true') {
        button.disabled = false;
        delete button.dataset.kitBlocked;
      }
    } else {
      button.disabled = true;
      button.dataset.kitBlocked = 'true';
      console.warn('[kit-customizer] incomplete bundle selection — add to cart blocked');
    }
  }

  /**
   * Record which component variants the current choices resolve to, so a post-order
   * automation can deduct their stock. Order follows the rows on the page.
   */
  #updateVariantIds() {
    // Queried, not a ref, for the same component-boundary reason as above.
    const variantIds = this.querySelector('input[name="properties[_kit_variant_ids]"]');
    if (!(variantIds instanceof HTMLInputElement)) return;

    const ids = [];
    for (const row of this.querySelectorAll('[data-kit-component]')) {
      const select = row.querySelector('[data-kit-select]');
      if (select instanceof HTMLSelectElement) {
        const id = select.selectedOptions[0]?.dataset.variantId;
        if (id) ids.push(id);
        continue;
      }
      const fixed = row.querySelector('[data-kit-fixed]');
      if (fixed instanceof HTMLInputElement && fixed.dataset.variantId) ids.push(fixed.dataset.variantId);
    }

    variantIds.value = ids.join(',');
  }

  /** @param {HTMLSelectElement} select */
  #updateThumbnail(select) {
    const row = select.closest('[data-kit-component]');
    const img = row?.querySelector('img.kit-component__img');
    const url = select.selectedOptions[0]?.dataset.image;
    if (!(img instanceof HTMLImageElement) || !url) return;

    // The srcset would otherwise keep winning over the new src.
    img.removeAttribute('srcset');
    img.src = url;
  }

  #updateSummary() {
    const { summary } = this.refs;
    if (!summary) return;

    if (!this.#isUpgrade) {
      summary.textContent = '';
      return;
    }

    const parts = [];
    // Only rows backed by a real product are choices worth summarising.
    for (const row of this.querySelectorAll('[data-kit-component][data-in-summary="true"]')) {
      const select = row.querySelector('[data-kit-select]');
      if (select instanceof HTMLSelectElement) {
        parts.push(select.selectedOptions[0]?.textContent?.trim() ?? '');
        continue;
      }
      const fixed = row.querySelector('[data-kit-fixed]');
      if (fixed instanceof HTMLInputElement && fixed.value) parts.push(fixed.value);
    }

    summary.textContent = parts.filter(Boolean).join(' · ');
  }
}

if (!customElements.get('kit-customizer-component')) {
  customElements.define('kit-customizer-component', KitCustomizer);
}
